import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Download, RefreshCw, Globe, Clock, MousePointerClick } from "lucide-react";
import { fmtDuration } from "@/lib/fmtDuration";
import EngagementKpis from "./EngagementKpis";

/**
 * UserActivityLog — live feed of human pageviews with source attribution.
 *
 * Sits right above `BotTrafficInsights` in /admin/stats. Honors the parent
 * range selector (today / yesterday / 7d / 30d / 90d) and ships a 30-day
 * CSV export regardless of the current view, so Rani can keep daily
 * monitoring compact while still pulling a full technical record on demand.
 */

// Category → tile color (consistent with the bot-card aesthetic).
const _CAT = {
  ai_assistant:   { bg: "#10A37F", fg: "#FFFFFF", label: "AI" },
  paid_search:    { bg: "#F4B400", fg: "#111827", label: "Ads" },
  organic_search: { bg: "#4285F4", fg: "#FFFFFF", label: "Search" },
  social:         { bg: "#1877F2", fg: "#FFFFFF", label: "Social" },
  email:          { bg: "#CC785C", fg: "#FFFFFF", label: "Email" },
  referral:       { bg: "#6E6B64", fg: "#FFFFFF", label: "Ref" },
  internal:       { bg: "#B08543", fg: "#FFFFFF", label: "In-site" },
  direct:         { bg: "#111827", fg: "#FFFFFF", label: "Direct" },
  team:           { bg: "#E5E1D8", fg: "#111827", text: "#6B6457", label: "Team" },
  other:          { bg: "#9CA3AF", fg: "#FFFFFF", label: "Other" },
};

const fmt = (n) => (n || 0).toLocaleString();

export default function UserActivityLog({ range = "last_week", rangeLabel = "Last 7 days" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/stats/user-activity", { params: { range, limit: 300 } })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [range]);

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/stats/user-activity.csv", {
        params: { range: "last_month" },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `propertic-user-activity-30d-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      console.error("User-activity CSV download failed", e);
      alert("CSV download failed. Please refresh and try again.");
    } finally {
      setDownloading(false);
    }
  };

  const items = data?.items || [];
  const topSources = useMemo(() => (data?.sources || []).slice(0, 6), [data]);

  return (
    <section className="mb-8" data-testid="user-activity-section">
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
        <div className="flex flex-wrap items-baseline gap-3">
          <h2 className="font-editorial text-lg sm:text-xl text-propertic-navy">User activity</h2>
          <span className="text-[10px] sm:text-[11px] text-propertic-muted font-mono-brand tracking-[0.12em] uppercase">
            {rangeLabel} · {fmt(data?.total || 0)} sessions · {fmt(data?.total_pageviews || 0)} pageviews
          </span>
        </div>
        <button
          onClick={downloadCsv}
          disabled={downloading}
          data-testid="user-activity-csv-export"
          className="h-8 px-3 rounded-lg bg-propertic-navy text-white text-[10px] font-mono-brand uppercase tracking-wide inline-flex items-center gap-1.5 disabled:opacity-60 hover:bg-propertic-navy-2 transition-colors"
          title="Download last 30 days of user activity"
        >
          {downloading
            ? <RefreshCw className="w-3 h-3 animate-spin" />
            : <Download className="w-3 h-3" />}
          Export CSV · 30D
        </button>
      </div>

      {!loading && <EngagementKpis engagement={data?.engagement} />}

      {/* Top sources strip — same visual rhythm as the bot cards above */}
      {!loading && topSources.length > 0 && (
        <div
          className="mb-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3"
          data-testid="top-sources-strip"
        >
          {topSources.map((s) => {
            const cat = _CAT[s.category] || _CAT.other;
            return (
              <div
                key={s.source}
                className="rounded-2xl border hairline bg-white p-3 flex items-center gap-3"
                data-testid={`source-chip-${s.source}`}
              >
                <div
                  className="min-w-[56px] h-9 px-2 rounded-xl flex items-center justify-center font-mono-brand text-[10px] flex-shrink-0"
                  style={{ backgroundColor: cat.bg, color: cat.fg }}
                >
                  {cat.label}
                </div>
                <div className="min-w-0">
                  <p className="font-mono-brand text-[9px] tracking-[0.12em] uppercase text-propertic-muted truncate">
                    {s.category.replace("_", " ")}
                  </p>
                  <p className="font-editorial text-lg text-propertic-navy tabular-nums leading-tight truncate" title={s.source}>
                    {s.source}
                  </p>
                  <p className="text-[10px] text-propertic-muted tabular-nums">{fmt(s.count)} sessions</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Live feed table */}
      <div className="bg-white rounded-2xl border hairline overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead className="text-propertic-muted bg-propertic-cream/40">
              <tr>
                <th className="text-start px-4 py-2 font-mono-brand text-[9px] uppercase tracking-wider whitespace-nowrap">Session start</th>
                <th className="text-start px-4 py-2 font-mono-brand text-[9px] uppercase tracking-wider">Source</th>
                <th className="text-start px-4 py-2 font-mono-brand text-[9px] uppercase tracking-wider">Top page (most time)</th>
                <th className="text-start px-4 py-2 font-mono-brand text-[9px] uppercase tracking-wider">Country</th>
                <th className="text-end   px-4 py-2 font-mono-brand text-[9px] uppercase tracking-wider whitespace-nowrap">Duration</th>
                <th className="text-end   px-4 py-2 font-mono-brand text-[9px] uppercase tracking-wider">Clicks</th>
              </tr>
            </thead>
            <tbody className="divide-y hairline">
              {loading && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-[11px] text-propertic-muted">
                  <RefreshCw className="w-3 h-3 animate-spin inline-block mr-2 align-[-2px]" /> Loading user activity…
                </td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-[11px] text-propertic-muted">
                  No visitor sessions in this window yet.
                </td></tr>
              )}
              {!loading && items.map((r, i) => {
                const cat = _CAT[r.source_category] || _CAT.other;
                return (
                  <tr key={r.session_key || i} className="hover:bg-propertic-cream/30" data-testid={`user-row-${i}`}>
                    <td className="px-4 py-2 font-mono-brand text-[10px] text-propertic-muted whitespace-nowrap">
                      {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-mono-brand"
                        style={{ backgroundColor: cat.bg + "22", color: cat.text || cat.bg }}
                        title={r.referer || ""}
                      >
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: cat.bg }} />
                        {r.source || "Unknown"}
                      </span>
                    </td>
                    <td
                      className="px-4 py-2 truncate max-w-[320px] font-mono-brand text-[11px]"
                      title={`${fmtDuration(r.top_path_seconds)} on this page · landed on ${r.landing_path} · ${r.pages_visited || 1} page(s) visited`}
                      data-testid={`user-row-top-page-${i}`}
                    >
                      {r.path || "/"}
                      {r.pages_visited > 1 && (
                        <span className="ml-2 text-[9px] text-propertic-muted">+{r.pages_visited - 1} more</span>
                      )}
                    </td>
                    <td className="px-4 py-2 font-mono-brand text-[10px] text-propertic-muted whitespace-nowrap">
                      <Globe className="w-3 h-3 inline-block mr-1 align-[-2px]" />
                      {r.country || "—"}
                    </td>
                    <td
                      className="px-4 py-2 text-right font-mono-brand text-[10px] tabular-nums whitespace-nowrap"
                      title={`Active engagement time · ${r.session_pageviews || 0} pageviews in this session`}
                      data-testid={`user-row-duration-${i}`}
                    >
                      <Clock className="w-3 h-3 inline-block mr-1 align-[-2px] text-propertic-muted" />
                      {fmtDuration(r.session_duration_s)}
                    </td>
                    <td
                      className="px-4 py-2 text-right font-mono-brand text-[10px] tabular-nums"
                      title="Deliberate clicks (throttled to max 1/sec)"
                    >
                      <MousePointerClick className="w-3 h-3 inline-block mr-1 align-[-2px] text-propertic-muted" />
                      {fmt(r.session_clicks)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
